<template>
  <div class="flex items-center gap-2 whitespace-nowrap w-full">
    <div :data-tippy-content="tooltipContent" class="w-full">
      <ProgressBar :segments="segments" class="!h-2" />
    </div>
    <div>
      {{ label }}
    </div>
    <!-- This hidden element is needed in order for the !text-green-400 class to be compiled -->
    <div class="hidden !text-green-400"></div>
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed } from "vue";
import {
  convertToTenantTimezone,
  customDateFormatWithTimezone
} from "@/lib/helpers";

import ProgressBar from "@/components/Seranova/ProgressBar/ProgressBar.vue";
import CONSTANTS from "@/Constants";
import { formatTimeDuration } from "@/mixins";
import { useScheduleColors } from "@/SchedulerV2/useScheduleColors";
import { Appointment } from "@/typings/scheduler";
const { APPOINTMENT_STATUSES } = CONSTANTS;

const { getStatusColor, getStatusCustomizationName } = useScheduleColors();
const props = defineProps<{ appointment: Appointment; label: string }>();

const preWorkMinutes = computed(() => {
  if (!durations.value.preWork) return null;
  return formatTimeDuration(durations.value.preWork);
});
const travelMinutes = computed(() => {
  if (!durations.value.travel) return null;
  return formatTimeDuration(durations.value.travel);
});
const workMinutes = computed(() => {
  if (!durations.value.work) return null;
  return formatTimeDuration(durations.value.work);
});
const durations = computed(() => {
  const { appointment } = props;
  const durations = {
    preWork: getDuration(
      appointment.pre_work_start_time,
      appointment.pre_work_end_time ||
        convertToTenantTimezone(new Date()).toISOString()
    ),
    travel: getDuration(
      appointment.travel_start_time,
      appointment.travel_end_time ||
        convertToTenantTimezone(new Date()).toISOString()
    ),
    work: getDuration(
      appointment.work_start_time,
      appointment.work_end_time ||
        convertToTenantTimezone(new Date()).toISOString()
    ),
    total: getTotalDuration()
  };
  return durations;
});
const segments = computed(() => {
  const status = getStatusCustomizationName(props.appointment);
  const segments = [] as {
    percentage: number;
    bgClass?: string;
    useBluePattern?: boolean;
    useRedPattern?: boolean;
    styles?: { background?: string; borderRight?: string };
  }[];
  if (preWorkMinutes.value) {
    segments.push({
      percentage: getPercentageofTotal(durations.value.preWork?.asMinutes()),
      styles: {
        background: getStatusColor(status),
        borderRight: "1px solid white"
      }
    });
  }
  if (travelMinutes.value) {
    segments.push({
      percentage: getPercentageofTotal(durations.value.travel?.asMinutes()),
      useBluePattern: status === "traveling",
      useRedPattern: status === "late",
      styles: {
        background: getStatusColor(status),
        borderRight: "1px solid white"
      }
    });
  }
  if (workMinutes.value) {
    segments.push({
      percentage: getPercentageofTotal(durations.value.work?.asMinutes()),
      styles: {
        background: getStatusColor(status)
      }
    });
  }
  return segments;
});
const tooltipContent = computed(
  () => `
<div>
  ${
    preWorkMinutes.value
      ? `<div>
    <span>Pre-Work</span>
    <span class="!text-${
      props.appointment.status === "accepted" &&
      !props.appointment.travel_start_time
        ? "green"
        : "gray"
    }-400">- ${preWorkMinutes.value}</span>
  </div>`
      : ""
  }
  ${
    travelMinutes.value
      ? `<div>
    <span>Travel</span>
    <span class="!text-${
      props.appointment.status === "accepted" &&
      props.appointment.travel_start_time
        ? "green"
        : "gray"
    }-400">- ${travelMinutes.value}</span>
  </div>`
      : ""
  }
  ${
    workMinutes.value
      ? `<div>
    <span>Working</span>
    <span class="!text-${
      props.appointment.status === "running" ? "green" : "gray"
    }-400">- ${workMinutes.value}</span>
  </div>`
      : ""
  }
  ${
    props.appointment.status === "completed"
      ? `<div>
    <span>Departed</span>
    <span class="!text-${
      props.appointment.status === "completed" ? "green" : "gray"
    }-400">- ${customDateFormatWithTimezone(
          props.appointment.work_end_time,
          "h:mm a"
        )}</span>
  </div>`
      : ""
  }
</div>
`
);
const getTotalDuration = () => {
  const { appointment } = props;
  if (appointment.status === APPOINTMENT_STATUSES.completed) {
    return getDuration(
      appointment.pre_work_start_time || appointment.travel_start_time,
      appointment.work_end_time
    );
  }
  return { asMinutes: () => appointment.estimated_duration_minutes };
};
const getDuration = (start?: string | null, end?: string | null) => {
  if (!start || !end) return null;
  return moment.duration(moment(end).diff(start));
};
const getPercentageofTotal = (minutes?: number): number => {
  if (!minutes) return 0;
  const total = durations.value.total?.asMinutes() || 1;
  const percent = (minutes / total) * 100;
  return percent;
};
</script>

<style scoped></style>
